// Paso 2 — lo que el archivo no dice.
//
// El aviso del mandante se lee AQUÍ y no en la tabla a propósito: la regla —la
// cabecera arrastra a las líneas, y cualquier línea puede llevar lo suyo— hay
// que entenderla antes de decidir la fecha, no después de haberla puesto mal.
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ClientePicker } from '@/components/ordenes-servicio/ClientePicker'
import { Loader } from '@/components/common/Loader'
import { ordenesVentaApi } from '@/services/api'
import type { AlmacenVenta, CabeceraBorrador } from '@/types'
import { MAX_REF_CLIENTE } from '@/utils/pedidoDesdeArchivo'
import { campoCls, cromado, etiquetaCls, pastillaCls } from './cromado'

interface Props {
  dark: boolean
  cab: CabeceraBorrador
  almacenes: AlmacenVenta[]
  intentado: boolean
  faltan: string[]
  onCambio: (patch: Partial<CabeceraBorrador>) => void
}

export function PasoCabecera({ dark, cab, almacenes, intentado, faltan, onCambio }: Props) {
  const { t } = useTranslation()
  const c = cromado(dark)

  // SIEMPRE con empresa. Sin ella el backend cae a la primera empresa permitida
  // —que por orden alfabético es `avi`— y para un cliente de otra entidad
  // devolvería una lista VACÍA de direcciones: no un error, una lista vacía,
  // que se lee como «este cliente no tiene direcciones».
  const direcciones = useQuery({
    queryKey: ['ov-direcciones', cab.clienteId, cab.empresa],
    queryFn: () => ordenesVentaApi.direcciones(cab.clienteId, cab.empresa),
    enabled: !!cab.clienteId && !!cab.empresa,
  })

  const falta = (codigo: string) => intentado && faltan.includes(codigo)

  // El largo se avisa EN CUANTO pasa, sin esperar a pulsar «siguiente»: el
  // usuario suele pegarlo del correo del cliente, y enterarse al final obliga a
  // volver a buscar de dónde lo sacó. El campo NO se recorta solo —eso le
  // cambiaría el dato sin decírselo— y `validarCabecera` impide avanzar.
  const largoRef = cab.numeroPedidoCliente.trim().length
  const refSePasa = largoRef > MAX_REF_CLIENTE

  return (
    <div className="space-y-5">
      {/* ── Cliente ─────────────────────────────────────────────────────── */}
      <div className={`px-4 py-3.5 rounded-xl border ${c.panel} ${c.panelBorde}`}>
        <div className="flex items-start justify-between gap-4 mb-2">
          <span className={`${etiquetaCls} ${c.mutedTxt}`}>
            {t('salesOrders.desdeArchivo.cliente')}
          </span>
          {/* Divisa y forma de pago: los dos salen del archivo y ninguno se
              edita. La divisa se queda aquí; la forma de pago VIAJA al flujo
              de creación (su `tipoPago`), así que se enseña rotulada — si sale
              mal, el pedido se crea con la forma de pago equivocada y eso no
              se ve en ninguna otra parte de esta pantalla. */}
          <div className="flex items-center gap-3 shrink-0">
            {cab.tipoPago && (
              <span className={`text-[10px] ${c.dimTxt}`}>
                {t('salesOrders.desdeArchivo.tipoPago')}:{' '}
                <span className={`font-mono ${c.mutedTxt}`}>{cab.tipoPago}</span>
              </span>
            )}
            {cab.moneda && (
              <span className={`text-[10px] font-mono ${c.dimTxt}`}>{cab.moneda}</span>
            )}
          </div>
        </div>

        {cab.clienteId ? (
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className={`text-[14px] font-semibold truncate ${c.mainTxt}`}>
                {cab.clienteNombre || cab.clienteId}
              </p>
              <p className={`text-[11px] font-mono mt-0.5 ${c.mutedTxt}`}>{cab.clienteId}</p>
            </div>
            <button
              type="button"
              onClick={() => onCambio({ clienteId: '', clienteNombre: '', direccionId: '', direccionTexto: '' })}
              className={`px-2.5 py-1.5 rounded-lg text-[11px] cursor-pointer transition-colors ${
                dark ? 'text-slate-400 hover:text-slate-200 hover:bg-white/6'
                     : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'
              }`}
            >
              {t('salesOrders.desdeArchivo.cambiar')}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className={`text-[11px] ${falta('falta_cliente') ? 'text-red-500' : c.mutedTxt}`}>
              {t('salesOrders.desdeArchivo.clienteNoReconocido')}
            </p>
            <ClientePicker
              dark={dark}
              onSelect={(cliente) => onCambio({
                clienteId: cliente.cuenta,
                clienteNombre: cliente.nombre,
                direccionId: '',
                direccionTexto: '',
              })}
            />
          </div>
        )}
      </div>

      {/* ── Dirección de entrega ────────────────────────────────────────── */}
      <div className="space-y-1.5">
        <label className={`${etiquetaCls} ${falta('falta_direccion') ? 'text-red-500' : c.mutedTxt}`}>
          {t('salesOrders.desdeArchivo.direccion')}
        </label>
        {!cab.clienteId ? (
          <p className={`text-[11px] ${c.dimTxt}`}>
            {t('salesOrders.desdeArchivo.direccionTrasCliente')}
          </p>
        ) : direcciones.isLoading ? (
          <Loader size={8} label={t('salesOrders.desdeArchivo.cargandoDirecciones')} />
        ) : (direcciones.data?.length ?? 0) === 0 ? (
          <p className={`text-[11px] ${dark ? 'text-amber-400' : 'text-amber-700'}`}>
            {t('salesOrders.desdeArchivo.sinDirecciones')}
          </p>
        ) : (
          <>
            <select
              value={cab.direccionId}
              onChange={(e) => {
                const elegida = direcciones.data?.find((d) => d.locationId === e.target.value)
                onCambio({
                  direccionId: e.target.value,
                  direccionTexto: elegida?.texto ?? '',
                })
              }}
              className={`${campoCls(dark, falta('falta_direccion'))} cursor-pointer`}
            >
              <option value="">{t('salesOrders.desdeArchivo.elige')}</option>
              {direcciones.data?.map((d) => (
                <option key={d.locationId} value={d.locationId}>
                  {d.descripcion || d.texto?.split('\n')[0] || d.locationId}
                </option>
              ))}
            </select>
            {cab.direccionTexto && (
              // El texto completo a la vista: es lo que se va a imprimir en el
              // albarán, y leerlo entero aquí es más barato que corregirlo luego.
              <p className={`text-[11px] whitespace-pre-line leading-relaxed mt-1.5 ${c.mutedTxt}`}>
                {cab.direccionTexto}
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Fecha y almacén ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className={`${etiquetaCls} ${falta('falta_fecha') ? 'text-red-500' : c.mutedTxt}`}>
            {t('salesOrders.desdeArchivo.fecha')}
          </label>
          <input
            type="date"
            value={cab.fecha}
            onChange={(e) => onCambio({ fecha: e.target.value })}
            className={campoCls(dark, falta('falta_fecha'))}
          />
        </div>
        <div className="space-y-1.5">
          <label className={`${etiquetaCls} ${falta('falta_almacen') ? 'text-red-500' : c.mutedTxt}`}>
            {t('salesOrders.desdeArchivo.almacen')}
          </label>
          <div className="flex gap-2 flex-wrap" role="radiogroup">
            {almacenes.map((a) => (
              <button
                key={a.codigo}
                type="button"
                role="radio"
                aria-checked={cab.almacen === a.codigo}
                onClick={() => onCambio({ almacen: a.codigo })}
                className={pastillaCls(dark, cab.almacen === a.codigo)}
              >
                {a.codigo}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* La regla que pidió el usuario, dicha antes de que importe. */}
      <div className={`flex gap-2.5 px-3.5 py-2.5 rounded-lg border ${
        dark ? 'bg-sky-500/[0.06] border-sky-500/15' : 'bg-sky-50 border-sky-200'
      }`}>
        <svg className={`w-3.5 h-3.5 shrink-0 mt-px ${dark ? 'text-sky-400' : 'text-sky-600'}`}
             viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" />
        </svg>
        <p className={`text-[11px] leading-relaxed ${dark ? 'text-sky-200/90' : 'text-sky-900'}`}>
          {t('salesOrders.desdeArchivo.mandante')}
        </p>
      </div>

      {/* ── Lo opcional ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className={`flex items-center gap-2 ${etiquetaCls} ${
            refSePasa ? 'text-red-500' : c.mutedTxt
          }`}>
            {t('salesOrders.desdeArchivo.numeroCliente')}
            <span className={`ml-auto font-normal tabular-nums ${
              refSePasa ? 'text-red-500' : c.dimTxt
            }`}>
              {largoRef}/{MAX_REF_CLIENTE}
            </span>
          </label>
          <input
            type="text"
            value={cab.numeroPedidoCliente}
            onChange={(e) => onCambio({ numeroPedidoCliente: e.target.value })}
            placeholder={t('salesOrders.desdeArchivo.opcional')}
            aria-invalid={refSePasa}
            className={campoCls(dark, refSePasa)}
          />
          {refSePasa && (
            <p className="text-[11px] text-red-500">
              {t('salesOrders.desdeArchivo.errores.referencia_cliente_larga', {
                max: MAX_REF_CLIENTE, largo: largoRef,
              })}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <label className={`${etiquetaCls} ${falta('copia_no_es_un_correo') ? 'text-red-500' : c.mutedTxt}`}>
            {t('salesOrders.desdeArchivo.copia')}
          </label>
          <input
            type="email"
            value={cab.copia}
            onChange={(e) => onCambio({ copia: e.target.value })}
            placeholder={t('salesOrders.desdeArchivo.copiaPista')}
            className={campoCls(dark, falta('copia_no_es_un_correo'))}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <label className={`${etiquetaCls} ${c.mutedTxt}`}>
          {t('salesOrders.desdeArchivo.nota')}
        </label>
        <textarea
          rows={2}
          value={cab.nota}
          onChange={(e) => onCambio({ nota: e.target.value })}
          placeholder={t('salesOrders.desdeArchivo.opcional')}
          className={`${campoCls(dark)} resize-none`}
        />
      </div>
    </div>
  )
}
